import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useFeaturedBusinesses } from "@/api/hooks";
import type { BusinessSummary } from "@/api/types";
import { Section } from "@/components/layout/section";
import { Button } from "@/components/ui/button";
import { CoverImage } from "@/components/ui/cover-placeholder";
import { Text } from "@/components/ui/text";

function FeaturedStudios() {
  const { data, isLoading, isError, isFetching, refetch } = useFeaturedBusinesses(4);
  const businesses = data?.businesses ?? [];

  if (!isLoading && !isError && businesses.length === 0) return null;

  return (
    <Section width="wide" id="featured" className="py-14 md:py-20">
      <div className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <Text as="h2" variant="display-l" className="text-[32px] md:text-[48px]">
          Salons on Slotique
        </Text>
        <Link
          to="/search"
          className="inline-flex min-h-11 items-center gap-2 whitespace-nowrap text-[14px] font-medium text-ink no-underline hover:text-electric-800 focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-4"
        >
          See all
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>

      {isLoading ? (
        <p role="status" className="text-[14px] text-slate">
          Loading salons…
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-x-10 md:gap-y-8">
          {businesses.map((business) => (
            <StudioRow key={business.id} business={business} />
          ))}
        </div>
      )}

      {isError && (
        <div role="alert" className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3">
          <p className="text-[16px] text-ink">
            {data ? "Salons couldn’t refresh." : "Salons couldn’t load."}
          </p>
          <Button onClick={() => void refetch()} disabled={isFetching} className="min-h-11">
            {isFetching ? "Trying again…" : "Try again"}
          </Button>
        </div>
      )}
    </Section>
  );
}

function StudioRow({ business }: { business: BusinessSummary }) {
  return (
    <Link
      to="/businesses/$slug"
      params={{ slug: business.slug }}
      className="group flex min-w-0 items-center gap-4 text-ink no-underline focus-visible:outline-2 focus-visible:outline-electric-700 focus-visible:outline-offset-4"
    >
      <CoverImage
        src={business.cover_image}
        name={business.name}
        decorative
        loading="lazy"
        className="w-28 shrink-0 rounded-sm sm:w-44"
      />
      <div className="min-w-0 flex-1">
        <h3 className="break-words font-display text-[18px] font-semibold leading-tight tracking-tight group-hover:underline group-hover:decoration-electric group-hover:underline-offset-4 sm:text-[20px]">
          {business.name}
        </h3>
        {(business.primary_category || business.location_city) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-slate">
            {business.primary_category && <span>{business.primary_category.name}</span>}
            {business.location_city && <span>{business.location_city}</span>}
          </div>
        )}
      </div>
    </Link>
  );
}

export { FeaturedStudios };
